import { useCallback, useEffect, useRef, useState } from "react";

import axios from "axios";

export interface UsePollingApiResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** Manually trigger a fetch and reset the interval timer. */
  refresh: () => void;
}

/**
 * usePollingApi — generic HTTP polling hook.
 *
 * Fetches `url` immediately on mount and then every `intervalMs` milliseconds.
 *
 * Behaviour:
 *  - Pauses automatically when `document.visibilityState === 'hidden'`
 *    (no background-tab requests).
 *  - Resumes (and fetches immediately) when the tab becomes visible again.
 *  - Deduplicates: if a fetch is already in-flight when the interval fires,
 *    the tick is skipped.
 *  - `refresh()` cancels the current in-flight request, fetches immediately,
 *    and resets the interval.
 *  - Cleans up AbortController and interval on unmount.
 *
 * @param url         Full URL to GET (with auth header added automatically).
 * @param intervalMs  Polling interval in milliseconds.
 * @param skip        When true, all fetching is suppressed (e.g. url not ready).
 */
export function usePollingApi<T>(
  url: string | null | undefined,
  intervalMs: number,
  skip = false,
): UsePollingApiResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inFlightRef = useRef(false);
  // Stable ref to url so callbacks always use the latest value
  const urlRef = useRef(url);
  urlRef.current = url;

  const doFetch = useCallback(async () => {
    if (inFlightRef.current || !urlRef.current || skip) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    inFlightRef.current = true;
    setLoading(true);

    // Track whether this specific request was aborted so the finally block
    // doesn't clobber loading state that belongs to the new request already
    // started by refresh().
    let wasAborted = false;
    try {
      const res = await axios.get<T>(urlRef.current, {
        signal: controller.signal,
        withCredentials: true,
      });
      setData(res.data);
      setError(null);
    } catch (err) {
      if (axios.isCancel(err)) {
        wasAborted = true;
        return; // aborted by refresh — not an error, new fetch already in flight
      }
      const message = axios.isAxiosError(err)
        ? (err.response?.data?.message ??
          err.response?.data?.error ??
          err.message)
        : err instanceof Error
          ? err.message
          : "Failed to load data.";
      setError(String(message));
    } finally {
      if (!wasAborted) {
        inFlightRef.current = false;
        setLoading(false);
      }
    }
  }, [skip]);

  const startInterval = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      if (document.visibilityState !== "hidden") {
        void doFetch();
      }
    }, intervalMs);
  }, [doFetch, intervalMs]);

  const refresh = useCallback(() => {
    abortRef.current?.abort();
    inFlightRef.current = false;
    void doFetch();
    startInterval();
  }, [doFetch, startInterval]);

  useEffect(() => {
    if (skip || !url) return;

    void doFetch();
    startInterval();

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void doFetch();
        startInterval();
      } else {
        if (timerRef.current) clearInterval(timerRef.current);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      if (timerRef.current) clearInterval(timerRef.current);
      abortRef.current?.abort();
      // Reset in-flight flag so the next mount's doFetch() isn't blocked.
      // Without this, React StrictMode's double-invoke leaves inFlightRef=true
      // after aborting, causing the remounted effect's fetch to be skipped and
      // loading to stay true indefinitely.
      inFlightRef.current = false;
    };
  }, [url, intervalMs, skip]);

  return { data, loading, error, refresh };
}
